import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toastFlash } from '@/lib/flash-toast';
import {
  type TimeSlotFormData,
  TimeSlotFormFields,
} from './TimeSlotFormFields';
import { toDateTimeLocal, toInstant } from './timeSlotForm';

// The Next dialog's defaults, kept verbatim.
const defaultValues = (): TimeSlotFormData => ({
  title: '',
  slug: '',
  description: '',
  startTime: toDateTimeLocal(new Date('2025-11-21T18:30:00-03:00')),
  endTime: toDateTimeLocal(new Date('2025-11-21T20:30:00-03:00')),
  location: '',
  color: '#e1ff00',
  target: [],
});

export function CreateTimeSlotDialog({ eventSlug }: { eventSlug: string }) {
  const [open, setOpen] = useState(false);
  const form = useForm<TimeSlotFormData>(defaultValues());
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.transform((values) => ({
      ...values,
      startTime: toInstant(values.startTime),
      endTime: toInstant(values.endTime),
    }));
    form.post(`/admin/${eventSlug}/time-slots`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Time slot created successfully!');
        setData(defaultValues());
        setOpen(false);
      },
      onError: () =>
        toast({ type: 'error', description: 'Failed to create time slot' }),
    });
  };

  const handleClose = () => {
    setOpen(false);
    setData(defaultValues());
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Create Time Slot
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Time Slot</DialogTitle>
            <DialogDescription>
              Add a new time slot to the event schedule.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <TimeSlotFormFields
              idPrefix="create-time-slot"
              data={data}
              onChange={(patch) => setData({ ...data, ...patch })}
              errors={errors}
              processing={processing}
            />

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create Time Slot'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
